// Threshold logic for the POS V2 short-viewport adaptive layout. Pure
// functions (no DOM access) so every boundary is unit-testable; components
// feed them vueuse useWindowSize/useElementSize measurements.
import { OrderType } from '@/enums/OrderEnums';

export const CART_TIER1_HEIGHT = 700;
export const CART_TIER2_BASE_HEIGHT = 580;
export const CART_TIER2_PER_EXTRA_FIELD = 100;
export const CART_TINY_HEIGHT = 460;
export const VIEW_COMPACT_HEIGHT = 700;
export const VIEW_COMPACT_MIN_WIDTH = 1024;
// Measured (es locale, empty state): full mode needs ~767px of bar width for
// Seat (3 labeled type buttons + table listbox + name input + covers chip +
// customer button), so labels drop below 780.
export const TOP_BAR_DROP_LABELS_BELOW = 780; // .top-bar-inner element width
// Collapse threshold = measured minimum of the no-labels layout per type
// (inline inputs bottom out at min-w 150 each): Seat ~667, Away ~567,
// Delivery (name+phone+notes+address chip) ~960.
export const TOP_BAR_COLLAPSE_INPUTS_BELOW: Record<OrderType, number> = {
  [OrderType.Seat]: 680,
  [OrderType.Away]: 590,
  [OrderType.Delivery]: 960,
};

/** What the optional club slot at the end of the bar is showing. */
export type TTopBarClubSlot = 'none' | 'button' | 'chip';

/**
 * Bar width the club slot consumes on top of the measured baselines above
 * (element minimum + the 8px flex gap). The attach button carries a visible
 * "Club" label (measured 88px in es/en + 8px gap); the attached-member chip
 * (two lines: name / points value + balance) truncates down to its 144px
 * min-width — the most the Seat collapsed layout can spare at a 1024px
 * window (bar 680: 176 type selector + 150 listbox + 44 covers + 2×54
 * buttons + 5 gaps leave 144).
 *
 * Locale note: the widest current posV2.metaClub translations ("Clube",
 * "Klubi", "Клуб") add ≲10px over the es/en measurement, absorbed by the
 * ~13px slack the mode thresholds carry. Re-measure if a longer translation
 * lands.
 */
export const TOP_BAR_CLUB_SLOT_EXTRA: Record<TTopBarClubSlot, number> = {
  none: 0,
  button: 96,
  chip: 152,
};
/**
 * Chip element widths under which it sheds detail to keep both figures
 * legible instead of truncating them: below COMPACT the second line drops
 * the "Puntos"/"Saldo" words (measured full wording "Puntos $ 12.500 |
 * Saldo $ 8.000" bold 12px + icon + remove button + padding needs ~250px);
 * below ICONLESS the leading star icon goes too (figures ~100px + icon 24 +
 * remove button, gaps and padding 48 = 172), so the figure-only variant
 * still fits the 144px minimum.
 */
export const TOP_BAR_CLUB_CHIP_COMPACT_BELOW = 250;
export const TOP_BAR_CLUB_CHIP_ICONLESS_BELOW = 176;
export const MONEY_STRIP_INLINE_ACTIONS_MIN = 560; // strip content width
/**
 * With an attached member the strip also carries the two figure-bearing
 * club buttons ("Puntos $3.500" / "Saldo $8.000"), so Descuento/Propina need
 * more room to stay inline; below the second threshold the club buttons drop
 * their words and keep icon + amount only; below the third the points button
 * also drops its "350 pts" sub-figure (measured: amount + Detalle + two
 * word-less club buttons + overflow + Pagar need ~595px, ~550px without the
 * sub-figure — the strip content is 544px wide at 1280 with the sidebar
 * open).
 */
export const MONEY_STRIP_INLINE_ACTIONS_MIN_WITH_CLUB = 760;
export const MONEY_STRIP_CLUB_LABELS_MIN = 680;
export const MONEY_STRIP_CLUB_SUB_MIN = 600;
/**
 * Member-less order: the club slot holds the "Club" identify button (star +
 * word, measured 78px + gap). Inline Descuento/Propina then need the base
 * 560 plus that (measured es: amount 129 + Detalle 69 + Club 78 + Descuento
 * 93 + Propina 74 + Pagar 61 + 6 gaps = ~552 content). The button itself is
 * the cue that a member can still be linked, so it outlives the other two:
 * with them folded into the overflow it needs amount + Detalle + Club +
 * overflow 44 + Pagar + 5 gaps = ~421 content; below that it joins the
 * overflow menu as "Identificar Club".
 */
export const MONEY_STRIP_INLINE_ACTIONS_MIN_WITH_ATTACH = 640;
export const MONEY_STRIP_CLUB_ATTACH_INLINE_MIN = 510;
/**
 * From this panel element width on, the Club member panel lays identity
 * (name over the identification cue) and the two tender buttons side by
 * side in one 48px row instead of stacking them. The tiles get two thirds
 * of the row: at 600px that is ~190px each, the same room they have on a
 * 390px phone, and the identity block keeps ~190px for the badge.
 */
export const CLUB_PANEL_ROW_MIN = 600;
/**
 * From this payments-box element width on, an expanded payment row lays the
 * amount beside the method name ("wide" row); below it the amount moves under
 * the method, next to the time. The wide row needs ~380px with no slack (28
 * icon + 120 method + 80 amount + 130 "Reembolsar" md button + gaps). Box
 * widths cluster in two populations: 326-394px (desktop money column 380/420
 * minus pane padding, and phones) versus 542px+ (1024x600 "Detalle" overlay,
 * tablets), and the box loses 24px when the order becomes paid (the flat
 * `.fully-paid` skin adds padding) — 480 splits the populations so no device
 * flips tiers when an order gets paid. Width 0 (hidden v-show pane) resolves
 * to the narrow layout, which is the safe default.
 */
export const PAYMENTS_BOX_WIDE_MIN = 480;
/**
 * From this caja-movements list element width on, a movement is a single line
 * (time, tile, details, amount, inline labeled action buttons); below it the
 * row is two lines (type · method / meta / notes) and tapping it opens the
 * actions dialog. The inline actions column is a 416px minimum ("Ver pedido"
 * + "Cambiar medio de pago" + "Reembolsar" label-only md buttons measure
 * ~406px in Spanish) and time/tile/amount/gaps take ~230px more, so the
 * details column keeps ~250px at 900 and ~270px on a 1024x600 tablet (939).
 * Measured list widths: phone 332, tablet portrait 694 (inline actions would
 * destroy the details column there), 1024x600 950, desktop 1110. Width 0
 * (before mount) resolves to the narrow layout, the safe default.
 */
export const CAJA_MOVEMENTS_WIDE_MIN = 900;

/**
 * What the strip's club slot is showing: the attached member's figure
 * buttons, the "Club" identify button of a member-less order, or nothing.
 */
export type TMoneyStripClubSlot = 'none' | 'actions' | 'attach';

const MONEY_STRIP_INLINE_ACTIONS_MIN_BY_SLOT: Record<
  TMoneyStripClubSlot,
  number
> = {
  none: MONEY_STRIP_INLINE_ACTIONS_MIN,
  actions: MONEY_STRIP_INLINE_ACTIONS_MIN_WITH_CLUB,
  attach: MONEY_STRIP_INLINE_ACTIONS_MIN_WITH_ATTACH,
};

export interface IMoneyStripLayout {
  /** Descuento/Propina inline (true) or inside the overflow menu (false). */
  inlineActions: boolean;
  /**
   * The "Club" identify button stays inline even when Descuento/Propina
   * have folded; false moves it into the overflow menu as well.
   */
  inlineClubAttach: boolean;
  /** Club buttons show their word labels next to the figures. */
  showClubLabels: boolean;
  /** Points button shows its "350 pts" sub-figure next to the amount. */
  showClubSub: boolean;
}

export function getMoneyStripLayout(
  stripWidth: number,
  clubSlot: TMoneyStripClubSlot
): IMoneyStripLayout {
  // useElementSize reports 0 until mounted: never flash the collapsed UI.
  if (stripWidth === 0) {
    return {
      inlineActions: true,
      inlineClubAttach: true,
      showClubLabels: true,
      showClubSub: true,
    };
  }
  return {
    inlineActions:
      stripWidth >= MONEY_STRIP_INLINE_ACTIONS_MIN_BY_SLOT[clubSlot],
    inlineClubAttach: stripWidth >= MONEY_STRIP_CLUB_ATTACH_INLINE_MIN,
    showClubLabels: stripWidth >= MONEY_STRIP_CLUB_LABELS_MIN,
    showClubSub: stripWidth >= MONEY_STRIP_CLUB_SUB_MIN,
  };
}

//#region Measurement-driven cart compaction (see use-cart-compaction.ts)

export type TCartTier = 0 | 1 | 2;

/** Minimum usable height for the cart lines list (~3 cart lines). */
export const CART_MIN_LINES_AREA = 190;
/**
 * Extra spare space (beyond restoring the blocks) required to de-escalate.
 * Absorbs gap-size differences between tiers and small measurement drift so
 * the state machine never oscillates around a boundary.
 */
export const CART_DEESCALATE_SLACK = 48;
/** Height difference between the 1-row and 2-row comment textarea. */
export const COMMENT_ROW_DELTA = 22;
/** A cached block measure is reused only within this width drift. */
export const BLOCK_CACHE_WIDTH_TOLERANCE = 4;

// Estimate fallbacks, only used before a block's first render (or after a
// cache invalidation). Verified against devtools at 1280x800; they only
// affect the pre-measurement frame and at most one corrective bounce.
export const EST_FIELDS_BASE = 28; // "Datos adicionales" section header
export const EST_FIELDS_PER_FIELD = 88; // label + input + gaps, single-line title
export const EST_COMMENT_1ROW = 62;
export const EST_COMMENT_2ROWS = 84;
export const EST_TOTALS_BASE = 56; // products + grand total rows
export const EST_TOTALS_PER_EXTRA_ROW = 18; // delivery/tax/discount/rounding

/**
 * One step of the cart compaction state machine, evaluated on every
 * measurement tick (ResizeObserver). Escalates while the lines area is below
 * the usable minimum; de-escalates only when restoring the next-richer
 * tier's blocks (nextBlocksDelta) still leaves MIN + SLACK of lines area.
 */
export function resolveCartTierStep(input: {
  tier: TCartTier;
  stableLinesArea: number;
  /** Extra height the next-richer tier needs; null when tier === 0. */
  nextBlocksDelta: number | null;
}): TCartTier {
  const { tier, stableLinesArea, nextBlocksDelta } = input;

  if (stableLinesArea < CART_MIN_LINES_AREA) {
    return tier === 0 ? 1 : 2;
  }

  if (
    tier > 0 &&
    nextBlocksDelta !== null &&
    stableLinesArea - nextBlocksDelta >=
      CART_MIN_LINES_AREA + CART_DEESCALATE_SLACK
  ) {
    return tier === 2 ? 1 : 0;
  }

  return tier;
}

/**
 * Cart footer region a tier transition unmounts (see PosV2Cart.vue):
 * - 'inline-editables': the inline custom-fields + comment blocks (any → 2).
 * - 'chips-row': the chips row subtree — de-escalating unmounts the tier-2
 *   editable popovers (2 → 1) or the whole row (1 → 0), and the popover
 *   panels are DOM children of the row.
 * - null: nothing focusable is lost (0 → 1 only swaps display-only totals).
 */
export type TTierUnmountRegion = 'inline-editables' | 'chips-row' | null;

/**
 * Which focusable region a from → to tier transition would unmount. The
 * compaction engine defers a transition while document.activeElement is
 * inside the returned region, so the keyboard-summoning input is never
 * destroyed under the user (focus loss would dismiss the keyboard and loop).
 */
export function getTierTransitionUnmounts(
  from: TCartTier,
  to: TCartTier
): TTierUnmountRegion {
  if (to === from) return null;
  if (to === 2) return 'inline-editables';
  if (to < from) return 'chips-row';
  return null;
}

/** A block's last rendered height, tagged with the context it was seen in. */
export interface ICartBlockMeasure {
  height: number;
  width: number;
  signature: string;
}

/**
 * Resolves a block's height from its cache entry when the context still
 * matches (column width within tolerance, same content signature);
 * otherwise falls back to the estimate.
 */
export function getBlockHeight(
  cached: ICartBlockMeasure | null,
  currentWidth: number,
  currentSignature: string,
  estimate: number
): number {
  if (
    cached &&
    Math.abs(cached.width - currentWidth) <= BLOCK_CACHE_WIDTH_TOLERANCE &&
    cached.signature === currentSignature
  ) {
    return cached.height;
  }
  return estimate;
}

//#endregion

/**
 * Tier-2 (fields collapse) threshold grows with the number of active custom
 * fields: each field beyond the first adds vertical footprint, so collapsing
 * must trigger earlier (i.e. at a taller viewport).
 */
export function getCartTier2Threshold(fieldCount: number): number {
  return (
    CART_TIER2_BASE_HEIGHT +
    Math.max(0, fieldCount - 1) * CART_TIER2_PER_EXTRA_FIELD
  );
}

/**
 * 0 = full footer; 1 = totals collapse into the Detalle chip; 2 = custom
 * fields + comment also collapse into chips. Tier 2 always implies tier 1.
 *
 * Pre-measurement seed only: it estimates the footer footprint from the
 * field count for the synchronous first paint. Once the cart reports real
 * element sizes, useCartCompaction's measurement-driven state machine
 * (resolveCartTierStep) owns the tier.
 */
export function getCartCompactionTier(
  height: number,
  fieldCount: number
): TCartTier {
  const tier2At = getCartTier2Threshold(fieldCount);
  const tier1At = Math.max(CART_TIER1_HEIGHT, tier2At);
  if (height <= tier2At) return 2;
  if (height <= tier1At) return 1;
  return 0;
}

export function isCartTiny(height: number): boolean {
  return height <= CART_TINY_HEIGHT;
}

export function isViewCompact(width: number, height: number): boolean {
  return width >= VIEW_COMPACT_MIN_WIDTH && height <= VIEW_COMPACT_HEIGHT;
}

export type TTopBarMode = 'full' | 'no-labels' | 'collapsed';

export function getTopBarMode(
  windowWidth: number,
  barWidth: number,
  orderType: OrderType,
  clubSlot: TTopBarClubSlot = 'none'
): TTopBarMode {
  // Mobile (<1024px window) keeps today's wrapping layout untouched.
  if (windowWidth < VIEW_COMPACT_MIN_WIDTH) return 'full';
  // useElementSize reports 0 until mounted; never flash the collapsed UI.
  if (barWidth <= 0) return 'full';
  // The club slot eats a fixed chunk of the bar; the thresholds describe the
  // rest of the layout, so compare against what it leaves available.
  const available = barWidth - TOP_BAR_CLUB_SLOT_EXTRA[clubSlot];
  if (available < TOP_BAR_COLLAPSE_INPUTS_BELOW[orderType]) return 'collapsed';
  if (available < TOP_BAR_DROP_LABELS_BELOW) return 'no-labels';
  return 'full';
}
